// Stelle auf den aktuellen Matching-Regelstand nachrechnen (nur Aivy-Admins).
// Backend: aivy-backend-app docs/matching-regelstaende.md, Baustein 3.
//
// spaceControl antwortet seitenweise: Jede Antwort zählt eine Seite der Stelle
// und bringt next_cursor, bis er null ist. Die erste Seite trägt total.

export const OUTCOMES = [
  'up',
  'down',
  'unchanged',
  'unscored',
  'failed',
  'skipped'
]

export const emptyRescoreSummary = () => ({
  total: null,
  processed: 0,
  current_revision: null,
  target_revision: null,
  ...Object.fromEntries(OUTCOMES.map((outcome) => [outcome, 0])),
  // Änderung in Score-Punkten (Backend ab #441). has_points bleibt false, solange
  // das Backend die Felder nicht liefert, dann zeigt der Dialog keinen Durchschnitt.
  has_points: false,
  up_points_sum: 0,
  up_points_max: 0,
  down_points_sum: 0,
  down_points_min: 0
})

/** Antwort von spaceControl (AWSJSON) auf das Ergebnis der Action */
export const parseSpaceControlResult = (raw, action) => {
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
  const res = parsed?.body?.[action]?.res
  if (!res) throw new Error(`spaceControl: keine Antwort für "${action}"`)
  return res
}

/** Antwort von careerControl (AWSJSON) auf das Ergebnis der Action */
export const parseCareerControlResult = (raw, action) => {
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
  const res = parsed?.[action]?.res
  if (!res) throw new Error(`careerControl: keine Antwort für "${action}"`)
  return res
}

export const addRescorePage = (summary, page) => {
  const next = { ...summary }
  if (typeof page.total === 'number') next.total = page.total
  next.current_revision = page.current_revision
  next.target_revision = page.target_revision
  OUTCOMES.forEach((outcome) => {
    next[outcome] += page[outcome] || 0
  })
  next.processed = OUTCOMES.reduce((acc, outcome) => acc + next[outcome], 0)
  if (typeof page.up_points_sum === 'number') {
    next.has_points = true
    next.up_points_sum += page.up_points_sum
    next.up_points_max = Math.max(next.up_points_max, page.up_points_max || 0)
    next.down_points_sum += page.down_points_sum || 0
    next.down_points_min = Math.min(
      next.down_points_min,
      page.down_points_min || 0
    )
  }
  return next
}

const signed = (points) =>
  `${points > 0 ? '+' : points < 0 ? '−' : '±'}${Math.abs(
    points
  ).toLocaleString('de-DE', { maximumFractionDigits: 1 })}`

/**
 * Durchschnitt und Extremwert einer Gruppe als kurzer Text, etwa
 * „Ø +3,2 Punkte · max. +10“. null, wenn es nichts zu zeigen gibt.
 */
export const pointsDetail = (summary, outcome) => {
  if (outcome !== 'up' && outcome !== 'down') return null
  if (!summary?.has_points) return null
  const count = summary[outcome]
  if (!count) return null
  const sum = summary[`${outcome}_points_sum`]
  const extreme =
    outcome === 'up' ? summary.up_points_max : summary.down_points_min
  const average = Math.round((sum / count) * 10) / 10
  return `Ø ${signed(average)} Punkte · max. ${signed(extreme)}`
}

/**
 * Fragt alle Seiten ab.
 * @param {object} args
 * @param {(cursor: string | null) => Promise<object>} args.fetchPage liefert eine Seite
 * @param {(summary: object) => void} [args.onProgress] nach jeder Seite
 * @param {() => boolean} [args.isCancelled] bricht vor der nächsten Seite ab
 */
export async function runRescorePages({ fetchPage, onProgress, isCancelled }) {
  let summary = emptyRescoreSummary()
  let cursor = null
  do {
    if (isCancelled?.()) return { summary, cancelled: true }
    const page = await fetchPage(cursor)
    summary = addRescorePage(summary, page)
    onProgress?.(summary)
    cursor = page.next_cursor
  } while (cursor)
  return { summary, cancelled: false }
}

// Ein Satz je Regelstand für den Dialog, damit Admins dem Kunden erklären
// können, was sich ändert. Quelle: RULESETS in aivy-backend-app
// sharedCode/rulesets. Ein neuer Regelstand braucht hier eine neue Zeile,
// ohne sie zeigt der Dialog nur die Nummer.
export const REVISION_SUMMARIES = {
  1: 'Interessen gestuft (0 / 0,5 / 1), im Tower of London zählen auch ungelöste Aufgaben nach ihren Zügen',
  2: 'Interessen stufenlos, Tower of London wie Regelstand 1',
  3: 'Interessen stufenlos, ungelöste Tower-of-London-Aufgaben zählen 0'
}

export const revisionSummary = (revision) =>
  REVISION_SUMMARIES[revision] || null

/** Der Button ergibt nur Sinn, wo matchMeIfYouCan v2 rechnet */
export const canRescoreCareer = (career) =>
  career?.diagnostic_version === 2 &&
  career?.status === 'ACTIVE' &&
  !career?.archived
